import React from "react";
import { cn } from "@/utils";
import { PixelEffect } from "@/components/ui/pixel-effect-dynamic";
import { DotsEffect } from "@/components/ui/dots-effect";

export type SectionBackgroundVariant =
  | "pixels"
  | "pixels-subtle"
  | "pixels-subtle-reverse"
  | "dots"
  | "none";

type PixelBackgroundVariant =
  | "pixels"
  | "pixels-subtle"
  | "pixels-subtle-reverse";

export default function Section({
  wrapperClassName,
  className,
  as,
  children,
  style,
  backgroundVariant = "none",
  id,
  ...props
}: {
  wrapperClassName?: string;
  className?: string;
  as?: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
  backgroundVariant?: SectionBackgroundVariant;
  id?: string;
}) {
  const SectionComponent = as || ("section" as React.ElementType);

  return (
    <SectionComponent
      className={cn("relative py-12 md:py-16 lg:py-24", wrapperClassName)}
      style={style}
      id={id}
      {...props}
    >
      {backgroundVariant === "dots" && (
        <div className="absolute inset-0 w-full h-full left-0 z-0">
          <div className="absolute inset-0 bg-background" />
          <DotsEffect className="z-[1]" />
        </div>
      )}
      {backgroundVariant !== "none" && backgroundVariant !== "dots" && (
        <div className="absolute inset-0 w-full h-full left-0 z-0">
          <BackgroundEffect variant={backgroundVariant} />
        </div>
      )}
      <div className={cn(className, "content relative z-10")}>{children}</div>
    </SectionComponent>
  );
}

function BackgroundEffect({ variant }: { variant: PixelBackgroundVariant }) {
  return (
    <div className="relative h-full w-full">
      <PixelEffect
        containerClassName="bg-gradient-to-br from-[#4E40FF]/50 via-[#2D4A9A]/50 to-[#0AFFC7]/50"
        colors={[
          [78, 64, 255],
          [10, 255, 199],
        ]}
        dotSize={3}
        opacities={[0.9, 1.0, 1.0]}
        showGradient={false}
        shader={`
        float animation_speed_factor = 100.0;
        float intro_offset = distance(u_resolution / 2.0 / u_total_size, st2) * 0.01 + (random(st2) * 0.15);
        opacity *= step(intro_offset, u_time * animation_speed_factor);
        opacity *= clamp((1.0 - step(intro_offset + 0.1, u_time * animation_speed_factor)) * 1.25, 1.0, 1.25);
        
        // Horizontal gradient for left-to-right transition
        vec2 normalizedPos = st / u_resolution;
        float gradientFactor = normalizedPos.x;
        
        // Smooth horizontal transition
        float smoothGradient = smoothstep(0.0, 1.0, gradientFactor);
        
        color = mix(u_colors[0], u_colors[3], smoothGradient);
      `}
      />

      <div className="absolute inset-0 w-full h-full bg-gradient-to-br from-background/80 via-background/80 to-background/90" />
      {/* {variant === "pixels-subtle" && (
        <div className="absolute inset-0 w-full h-full bg-gradient-to-b from-background/60 via-background/30 via-50% to-background/50" />
      )} */}
      {variant === "pixels-subtle-reverse" && (
        <div className="absolute inset-0 w-full h-full bg-gradient-to-t from-background/70 via-background/0 via-20% to-background/60" />
      )}
    </div>
  );
}
